import React from "react"
import { StateMachineProvider, StateMachineStepView, useStateMachine } from "../../context/stateMachine/StateMachineV2"
import {
  caseRateRenewalSteps,
  caseRateRenewalWorkflows,
  Steps as CaseRateRenewalSteps,
  Workflows as CaseRateRenewalWorkflows,
  Sections
} from "./workflow"
import {
  AdvancedCheckInWarningQueryString,
  CaseRateRenewalsContextProvider,
  useCaseRateRenewals
} from "./useCaseRateRenewals"
import useCarrotPlan from "../hooks/useCarrotPlan"
import { useHistory } from "react-router-dom"
import queryString from "query-string"
import { getHeap } from "../../../utils/heap"
import { AdvancedCheckInWarning } from "./AdvancedCheckInWarning"
import { Box, CloseIcon, Dialog, IconButton, useMediaQuery, useTheme } from "@carrotfertility/carotene-core"
import { useIntl } from "react-intl"
import { useRefetchRenewalData } from "#/components/carrot-plans/case-rate-renewals/useQueryCaseRateRenewals"

type CarrotPlansGatedAccountContainerProps = {
  shouldCrrModalRender: boolean
  showAdvancedCheckInFeatures: boolean
  isCrrAdvancedCheckIn: boolean
}

type CarrotPlansGatedAccountModalProps = {
  showAdvancedCheckInFeatures: boolean
  isCrrAdvancedCheckIn: boolean
}

function CarrotPlansGatedAccountModal({
  showAdvancedCheckInFeatures,
  isCrrAdvancedCheckIn
}: CarrotPlansGatedAccountModalProps): JSX.Element {
  const { openGatedAccountModal, disableModalCloseOnLoading } = useCaseRateRenewals()
  const history = useHistory()
  const { step } = queryString.parse(window.location.search)
  const showAdvancedCheckInCloseButton =
    showAdvancedCheckInFeatures && step !== AdvancedCheckInWarningQueryString && !disableModalCloseOnLoading

  if (isCrrAdvancedCheckIn) {
    getHeap().track("CaseRateRenewalsAdvancedCheckIn", { EventName: "Advanced check in opened" })
  }

  const caseRateRenewalSection = {
    name: Sections.CASE_RATE_RENEWAL,
    initialStep: CaseRateRenewalSteps.CARROT_PLAN_OPTIONS,
    desiredWorkflow: CaseRateRenewalWorkflows.CASE_RATE_RENEWAL,
    steps: caseRateRenewalSteps,
    workflows: caseRateRenewalWorkflows
  }

  const handleClose = showAdvancedCheckInFeatures
    ? () => history.push(`/?step=${AdvancedCheckInWarningQueryString}`)
    : null

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <Dialog open={openGatedAccountModal} onClose={handleClose} fullScreen={fullScreen}>
      <StateMachineProvider
        initialSection={Sections.CASE_RATE_RENEWAL}
        stateMachineDescription={[caseRateRenewalSection]}
        onComplete={null}
      >
        <Box display="flex" justifyContent="flex-end">
          {showAdvancedCheckInCloseButton && <AdvancedCheckInCloseButton />}
        </Box>

        <Box data-testid="crr-modal">
          {step === AdvancedCheckInWarningQueryString ? <AdvancedCheckInWarning /> : <StateMachineStepView />}
        </Box>
      </StateMachineProvider>
    </Dialog>
  )
}

function AdvancedCheckInCloseButton(): JSX.Element {
  const intl = useIntl()
  const history = useHistory()
  const refetchData = useRefetchRenewalData()
  const { stepName } = useStateMachine(null)
  const isStepAfterCRRActionPerformed =
    stepName === CaseRateRenewalSteps.THANKS_FOR_LETTING_US_KNOW || stepName === CaseRateRenewalSteps.KEEP_MY_PLAN
  const handleClose = () =>
    isStepAfterCRRActionPerformed ? refetchData() : history.push(`/?step=${AdvancedCheckInWarningQueryString}`)

  return (
    <IconButton aria-label={intl.formatMessage({ defaultMessage: "close" })} onClick={handleClose}>
      <CloseIcon />
    </IconButton>
  )
}

export function CarrotPlansGatedAccountContainer({
  shouldCrrModalRender,
  showAdvancedCheckInFeatures,
  isCrrAdvancedCheckIn
}: CarrotPlansGatedAccountContainerProps): JSX.Element {
  const { data: carrotPlan, isLoading: carrotPlanIsLoading } = useCarrotPlan()

  return !carrotPlanIsLoading && carrotPlan ? (
    <CaseRateRenewalsContextProvider shouldCrrModalRender={shouldCrrModalRender}>
      <CarrotPlansGatedAccountModal
        showAdvancedCheckInFeatures={showAdvancedCheckInFeatures}
        isCrrAdvancedCheckIn={isCrrAdvancedCheckIn}
      />
    </CaseRateRenewalsContextProvider>
  ) : null
}
